import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Input,
  InputNumber,
  List,
  Modal,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Steps,
  Tag,
  message,
} from 'antd';
import {
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { apiClient } from '../../lib/api-client';
import { DOCUMENT_ENTITY_TYPE_SUPPLIER_ASSESSMENT } from '@/constants/document-entity-types';
import ProcurementAttachmentsCard from '@/components/procurement/ProcurementAttachmentsCard';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { SmartBreadcrumbs } from '@/components/Breadcrumbs';
import {
  getAssessmentResultTag,
  getAssessmentScoreColor,
  getAssessmentStatusTag,
  getAssessmentWorkflowStep,
  normalizeAssessment,
  num,
} from './supplierAssessmentUi';

const { Option } = Select;
const { Step } = Steps;
const { TextArea } = Input;

const LIST_PATH = '/scm/quality/supplier-assessments';

export const SupplierAssessmentDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [assessment, setAssessment] = useState<any>(null);
  const [responseModalVisible, setResponseModalVisible] = useState(false);
  const [evaluateModalVisible, setEvaluateModalVisible] = useState(false);
  const [approveModalVisible, setApproveModalVisible] = useState(false);
  const [classifyModalVisible, setClassifyModalVisible] = useState(false);
  const [responseForm] = Form.useForm();
  const [evaluateForm] = Form.useForm();
  const [approveForm] = Form.useForm();
  const [classifyForm] = Form.useForm();

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await apiClient.get(`/procurement/supplier-assessments/${id}`);
      const detail =
        response && typeof response === 'object' && 'data' in response
          ? (response as { data: unknown }).data
          : response;
      setAssessment(normalizeAssessment((detail || {}) as Record<string, any>));
    } catch {
      message.error('Failed to fetch assessment details');
      setAssessment(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSubmitResponse = async (values: any, questionId: string) => {
    if (!assessment) return;
    try {
      const question = (assessment.questions || []).find((q: any) => q.id === questionId);
      const rawValue = values.response_value ?? values.response_text ?? '';
      let boolean_value: boolean | undefined;
      if (question?.question_type === 'yes-no') {
        const v = String(rawValue).trim().toLowerCase();
        if (['yes', 'y', 'true', '1'].includes(v)) boolean_value = true;
        else if (['no', 'n', 'false', '0'].includes(v)) boolean_value = false;
      }
      const numericCandidate = Number(rawValue);
      const numeric_value =
        question?.question_type === 'number' || question?.question_type === 'rating'
          ? Number.isFinite(numericCandidate)
            ? numericCandidate
            : undefined
          : undefined;

      await apiClient.post(`/procurement/supplier-assessments/${assessment.id}/responses`, {
        question_id: questionId,
        response_value: rawValue,
        response_text: values.response_text,
        score_achieved:
          values.score_achieved != null && values.score_achieved !== ''
            ? Number(values.score_achieved)
            : undefined,
        numeric_value,
        boolean_value,
        evidence_url: values.evidence_url,
        comments: values.response_text,
      });
      message.success('Response submitted successfully');
      setResponseModalVisible(false);
      responseForm.resetFields();
      await load();
    } catch (error: any) {
      message.error(
        error?.response?.data?.error || error?.response?.data?.message || 'Failed to submit response'
      );
    }
  };

  const handleSubmitAssessment = async () => {
    if (!assessment?.id) return;
    try {
      await apiClient.post(`/procurement/supplier-assessments/${assessment.id}/submit`, {});
      message.success('Assessment submitted successfully');
      await load();
    } catch {
      message.error('Failed to submit assessment');
    }
  };

  const handleEvaluateAssessment = async (values: any) => {
    if (!assessment) return;
    try {
      await apiClient.post(`/procurement/supplier-assessments/${assessment.id}/evaluate`, {
        reviewer_name: values.reviewer_name,
        reviewer_comments: values.reviewer_comments,
        recommendation: values.recommendation || 'Approve',
        evaluator_comments: values.reviewer_comments,
      });
      message.success('Assessment evaluated successfully');
      setEvaluateModalVisible(false);
      evaluateForm.resetFields();
      await load();
    } catch {
      message.error('Failed to evaluate assessment');
    }
  };

  const handleApproveAssessment = async (values: any) => {
    if (!assessment) return;
    try {
      await apiClient.post(`/procurement/supplier-assessments/${assessment.id}/approve`, {
        approver_comments: values.approver_comments,
      });
      message.success('Assessment approved successfully');
      setApproveModalVisible(false);
      approveForm.resetFields();
      await load();
    } catch {
      message.error('Failed to approve assessment');
    }
  };

  const handleClassifySupplier = async (values: any) => {
    try {
      await apiClient.post(`/procurement/suppliers/${values.supplier_id}/classify`, {
        classification_type: values.classification_type,
        classification_reason: values.classification_reason,
        reevaluation_frequency_months: values.reevaluation_frequency_months,
        critical_justification: values.critical_justification,
      });
      message.success('Supplier classified successfully');
      setClassifyModalVisible(false);
      classifyForm.resetFields();
    } catch {
      message.error('Failed to classify supplier');
    }
  };

  if (loading && !assessment) {
    return <Spin style={{ display: 'block', margin: '48px auto' }} />;
  }

  if (!assessment) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="error"
          showIcon
          message="Assessment not found"
          action={
            <Button onClick={() => navigate(LIST_PATH)}>Back to assessments</Button>
          }
        />
      </div>
    );
  }

  const canApprove =
    assessment.status === 'Evaluated' ||
    (assessment.status === 'Completed' &&
      assessment.assessment_result === 'Pass' &&
      !assessment.approved_at);

  return (
    <div style={{ margin: -24, minHeight: 'calc(100vh - 64px)' }}>
      <div style={{ padding: '8px 16px 0' }}>
        <SmartBreadcrumbs />
      </div>
      <WorkspaceLayout
        fullBleed
        header={
          <EnterprisePageHeader
            documentLabel="Supplier Assessment"
            documentNumber={assessment.assessment_number || id}
            statusTag={
              <Space wrap>
                {getAssessmentStatusTag(assessment.status)}
                {assessment.assessment_result
                  ? getAssessmentResultTag(assessment.assessment_result)
                  : null}
              </Space>
            }
            subtitle={
              <span>
                {assessment.supplier_name || '—'} · {assessment.template_name || '—'}
              </span>
            }
            primaryActions={
              <Space wrap>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(LIST_PATH)}>
                  Assessments
                </Button>
                <Button icon={<ReloadOutlined />} onClick={() => void load()} loading={loading}>
                  Refresh
                </Button>
                {assessment.status === 'Draft' ? (
                  <Button type="primary" onClick={() => void handleSubmitAssessment()}>
                    Submit assessment
                  </Button>
                ) : null}
                {assessment.status === 'Submitted' || assessment.status === 'Under-Review' ? (
                  <Button type="primary" onClick={() => setEvaluateModalVisible(true)}>
                    Evaluate
                  </Button>
                ) : null}
                {canApprove ? (
                  <Button type="primary" onClick={() => setApproveModalVisible(true)}>
                    Approve
                  </Button>
                ) : null}
                {assessment.status === 'Approved' || Boolean(assessment.approved_at) ? (
                  <Button
                    type="primary"
                    icon={<TrophyOutlined />}
                    onClick={() => {
                      classifyForm.setFieldsValue({ supplier_id: assessment.supplier_id });
                      setClassifyModalVisible(true);
                    }}
                  >
                    Classify supplier
                  </Button>
                ) : null}
              </Space>
            }
          />
        }
      >
        <div style={{ padding: '0 16px 24px' }}>
          <Steps current={getAssessmentWorkflowStep(assessment)} style={{ marginBottom: 24 }}>
            <Step title="Draft" description="In progress" />
            <Step title="Submitted" description="Under review" />
            <Step title="Evaluated" description="Scored" />
            <Step title="Approved" description="Final approval" />
          </Steps>

          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic
                  title="Overall Score"
                  value={num(assessment.score_percentage).toFixed(1)}
                  suffix="%"
                  valueStyle={{ color: getAssessmentScoreColor(assessment.score_percentage) }}
                  prefix={
                    assessment.assessment_result === 'Pass' ? (
                      <CheckCircleOutlined />
                    ) : (
                      <CloseCircleOutlined />
                    )
                  }
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic
                  title="Points Achieved"
                  value={num(assessment.total_score)}
                  suffix={`/ ${num(assessment.max_possible_score, 100)}`}
                />
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic title="Status" value={assessment.status} valueStyle={{ fontSize: 16 }} />
                {getAssessmentStatusTag(assessment.status)}
              </Card>
            </Col>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic
                  title="Result"
                  value={assessment.assessment_result || 'Pending'}
                  valueStyle={{ fontSize: 16 }}
                />
                {assessment.assessment_result
                  ? getAssessmentResultTag(assessment.assessment_result)
                  : null}
              </Card>
            </Col>
          </Row>

          <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }} style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Supplier">{assessment.supplier_name}</Descriptions.Item>
            <Descriptions.Item label="Template">{assessment.template_name}</Descriptions.Item>
            <Descriptions.Item label="Assessor">
              {assessment.assessor_name ||
                (typeof assessment.evaluator_comments === 'string' &&
                assessment.evaluator_comments.includes('Assessor:')
                  ? assessment.evaluator_comments
                      .split('\n')
                      .find((l: string) => l.startsWith('Assessor:'))
                      ?.replace('Assessor:', '')
                      .trim()
                  : '-') ||
                '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Assessment Date">
              {assessment.assessment_date
                ? dayjs(assessment.assessment_date).format('DD MMM YYYY')
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Location" span={2}>
              {assessment.assessment_location ||
                (typeof assessment.evaluator_comments === 'string' &&
                assessment.evaluator_comments.includes('Location:')
                  ? assessment.evaluator_comments
                      .split('\n')
                      .find((l: string) => l.startsWith('Location:'))
                      ?.replace('Location:', '')
                      .trim()
                  : '-') ||
                '-'}
            </Descriptions.Item>
          </Descriptions>

          {assessment.questions && assessment.questions.length > 0 ? (
            <Card title="Assessment Questions & Responses" style={{ marginBottom: 16 }}>
              <List
                dataSource={assessment.questions}
                renderItem={(question: any, index: number) => (
                  <List.Item
                    actions={
                      assessment.status === 'Draft'
                        ? [
                            <Button
                              key="resp"
                              type="link"
                              onClick={() => {
                                const maxScore = num(question.max_score ?? question.weight, 1);
                                responseForm.setFieldsValue({
                                  questionId: question.id,
                                  response_value: question.response?.response_value || '',
                                  response_text: question.response?.comments || '',
                                  score_achieved:
                                    question.response?.score_achieved ??
                                    question.response?.score ??
                                    maxScore,
                                  evidence_url: question.response?.evidence_url || '',
                                  _question_type: question.question_type,
                                  _max_score: maxScore,
                                  _question_text: question.question_text,
                                });
                                setResponseModalVisible(true);
                              }}
                            >
                              {question.response ? 'Edit Response' : 'Add Response'}
                            </Button>,
                          ]
                        : []
                    }
                  >
                    <List.Item.Meta
                      avatar={<div style={{ fontSize: 18, fontWeight: 'bold' }}>Q{index + 1}</div>}
                      title={
                        <Space wrap>
                          {question.question_text}
                          {question.is_mandatory && <Tag color="red">Mandatory</Tag>}
                          <Tag>{question.question_category}</Tag>
                        </Space>
                      }
                      description={
                        <div>
                          <div>
                            Max Score: {num(question.max_score, 1)} | Weight: {num(question.weight, 1)}
                          </div>
                          {question.response ? (
                            <div style={{ marginTop: 8 }}>
                              <strong>Response:</strong>{' '}
                              {question.response.response_text ||
                                question.response.response_value ||
                                '—'}
                              <br />
                              <strong>Score:</strong>{' '}
                              <Tag
                                color={
                                  num(question.response.score_achieved) >=
                                  num(question.max_score, 1) * 0.7
                                    ? 'green'
                                    : 'orange'
                                }
                              >
                                {num(question.response.score_achieved)} / {num(question.max_score, 1)}
                              </Tag>
                            </div>
                          ) : null}
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            </Card>
          ) : null}

          {assessment.reviewer_comments ? (
            <Alert
              message="Reviewer Comments"
              description={assessment.reviewer_comments}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          ) : null}

          {assessment.approver_comments ? (
            <Alert
              message="Approver Comments"
              description={assessment.approver_comments}
              type="success"
              showIcon
              style={{ marginBottom: 16 }}
            />
          ) : null}

          <ProcurementAttachmentsCard
            entityType={DOCUMENT_ENTITY_TYPE_SUPPLIER_ASSESSMENT}
            entityId={assessment.id}
            queryKeyPrefix={['documents', 'supplier-assessment']}
          />
        </div>
      </WorkspaceLayout>

      <Modal
        title="Submit Response"
        open={responseModalVisible}
        onCancel={() => {
          setResponseModalVisible(false);
          responseForm.resetFields();
        }}
        onOk={() => {
          const questionId = responseForm.getFieldValue('questionId');
          responseForm.validateFields().then((values) => {
            void handleSubmitResponse(values, questionId);
          });
        }}
        destroyOnHidden
      >
        <Form form={responseForm} layout="vertical">
          <Form.Item name="questionId" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="_question_type" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="_max_score" hidden>
            <InputNumber />
          </Form.Item>
          <Form.Item name="_question_text" hidden>
            <Input />
          </Form.Item>

          <Form.Item shouldUpdate noStyle>
            {() => {
              const qText = responseForm.getFieldValue('_question_text');
              const qType = responseForm.getFieldValue('_question_type');
              const maxScore = num(responseForm.getFieldValue('_max_score'), 1);
              return (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message={qText || 'Question'}
                  description={`Type: ${qType || 'text'} · Max score: ${maxScore}`}
                />
              );
            }}
          </Form.Item>

          <Form.Item shouldUpdate noStyle>
            {() => {
              const qType = responseForm.getFieldValue('_question_type');
              if (qType === 'yes-no') {
                return (
                  <Form.Item
                    name="response_value"
                    label="Response"
                    rules={[{ required: true, message: 'Please select Yes or No' }]}
                  >
                    <Radio.Group
                      onChange={(e) => {
                        const maxScore = num(responseForm.getFieldValue('_max_score'), 1);
                        responseForm.setFieldsValue({
                          score_achieved: e.target.value === 'Yes' ? maxScore : 0,
                        });
                      }}
                    >
                      <Radio value="Yes">Yes</Radio>
                      <Radio value="No">No</Radio>
                    </Radio.Group>
                  </Form.Item>
                );
              }
              return (
                <Form.Item
                  name="response_value"
                  label="Response Value"
                  rules={[{ required: true, message: 'Please enter a response' }]}
                >
                  <Input placeholder={qType === 'number' ? 'e.g., 12' : 'Your answer'} />
                </Form.Item>
              );
            }}
          </Form.Item>

          <Form.Item name="response_text" label="Detailed Response / Comments">
            <TextArea rows={3} placeholder="Optional details..." />
          </Form.Item>

          <Form.Item
            name="score_achieved"
            label="Score Achieved"
            rules={[{ required: true, message: 'Please enter score' }]}
            extra="Defaults to full marks when answered; adjust if needed."
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              max={num(responseForm.getFieldValue('_max_score'), 100)}
            />
          </Form.Item>

          <Form.Item name="evidence_url" label="Evidence URL">
            <Input placeholder="URL to supporting evidence" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Evaluate Assessment"
        open={evaluateModalVisible}
        onCancel={() => {
          setEvaluateModalVisible(false);
          evaluateForm.resetFields();
        }}
        onOk={() => evaluateForm.submit()}
        destroyOnHidden
      >
        <Form form={evaluateForm} layout="vertical" onFinish={handleEvaluateAssessment}>
          <Form.Item
            name="reviewer_name"
            label="Reviewer Name"
            rules={[{ required: true, message: 'Please enter name' }]}
          >
            <Input placeholder="Name of reviewer (QHSE Engineer)" />
          </Form.Item>
          <Form.Item
            name="recommendation"
            label="Recommendation"
            initialValue="Approve"
            rules={[{ required: true, message: 'Please select a recommendation' }]}
          >
            <Radio.Group>
              <Radio value="Approve">Approve (Qualify supplier)</Radio>
              <Radio value="Conditional-Approval">Conditional</Radio>
              <Radio value="Re-assess">Re-assess</Radio>
              <Radio value="Reject">Reject</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item name="reviewer_comments" label="Review Comments">
            <TextArea rows={5} placeholder="Enter review comments and findings..." />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Approve Assessment"
        open={approveModalVisible}
        onCancel={() => {
          setApproveModalVisible(false);
          approveForm.resetFields();
        }}
        onOk={() => approveForm.submit()}
        destroyOnHidden
      >
        <Alert
          message="Final Approval"
          description="You are about to give final approval to this supplier assessment. This action will update the supplier's qualification status."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form form={approveForm} layout="vertical" onFinish={handleApproveAssessment}>
          <Form.Item name="approver_comments" label="Approval Comments">
            <TextArea rows={4} placeholder="Enter approval comments..." />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Classify Supplier"
        open={classifyModalVisible}
        onCancel={() => {
          setClassifyModalVisible(false);
          classifyForm.resetFields();
        }}
        onOk={() => classifyForm.submit()}
        destroyOnHidden
      >
        <Form form={classifyForm} layout="vertical" onFinish={handleClassifySupplier}>
          <Form.Item name="supplier_id" hidden>
            <Input />
          </Form.Item>
          <Form.Item
            name="classification_type"
            label="Classification"
            rules={[{ required: true, message: 'Please select classification' }]}
          >
            <Radio.Group>
              <Radio value="Critical">Critical Supplier</Radio>
              <Radio value="Non-Critical">Non-Critical Supplier</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            name="reevaluation_frequency_months"
            label="Re-evaluation Frequency (months)"
            rules={[{ required: true }]}
            initialValue={24}
          >
            <Select>
              <Option value={12}>12 months (Non-Critical)</Option>
              <Option value={24}>24 months (Critical)</Option>
              <Option value={36}>36 months</Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="classification_reason"
            label="Classification Reason"
            rules={[{ required: true }]}
          >
            <TextArea rows={3} placeholder="Reason for this classification..." />
          </Form.Item>
          <Form.Item name="critical_justification" label="Critical Justification (if Critical)">
            <TextArea rows={3} placeholder="Justify critical classification..." />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SupplierAssessmentDetailPage;
